import type { VariablesOf } from '@graphql-typed-document-node/core';
import type { LinkedInstitution, LinkExitError } from '@moneykit/connect';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useEffect, useRef, useState } from 'react';
import MoneyKit from '@moneykit/connect';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import toast from 'react-hot-toast';
import { MoneykitConnectDefaultButton } from 'src/components/moneykit/moneykit-connect-default-button';
import { UpdateType } from 'src/constants/portfolio';
import { graphql } from 'src/gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import type { ButtonProps } from '@savvy-kit/components/button';
import type { ID } from '@savvy/utils/id';
import { primaryColors } from '@savvy/deprecated-shared/theme';
import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { MoneykitConnectFailure } from './moneykit-connect-failure';
import { useHouseholdWriteAccess } from '../../hooks/use-household-write-access';
import TrackedButton from '../tracked/tracked-button';
import {
  buildMoneykitExchangeLinkVariables,
  hasMagicLinkAuthorizationHeader,
} from './build-moneykit-exchange-link-variables';
import {
  mergeMoneykitConnectFailure,
  moneykitConnectFailureMessage,
  readMoneykitConnectEventFailure,
  reportMoneykitConnectFailure,
} from './moneykit-connect-failure';
import { fitMoneykitConnectFrameToViewport } from './moneykit-connect-frame';
import { startMoneykitConnectKeepAlive } from './moneykit-connect-keep-alive';
import { readMoneykitConnectEventInstitutionName, readMoneykitInstitutionName } from './moneykit-institution-name';
import {
  MONEYKIT_OAUTH_CLIENT_USER_ID_KEY,
  MONEYKIT_OAUTH_PROVIDER_ACCOUNT_ID_KEY,
  MONEYKIT_OAUTH_REPLACING_PROVIDER_ACCOUNT_ID_KEY,
  MONEYKIT_OAUTH_RETURN_PAGE_KEY,
  MONEYKIT_OAUTH_SELECTED_INSTITUTION_NAME_KEY,
  MONEYKIT_OAUTH_TOKEN_KEY,
} from './moneykit-oauth-storage-keys';

export {
  MONEYKIT_OAUTH_CLIENT_USER_ID_KEY,
  MONEYKIT_OAUTH_PROVIDER_ACCOUNT_ID_KEY,
  MONEYKIT_OAUTH_REPLACING_PROVIDER_ACCOUNT_ID_KEY,
  MONEYKIT_OAUTH_RESUME_HREF_KEY,
  MONEYKIT_OAUTH_RETURN_PAGE_KEY,
  MONEYKIT_OAUTH_SELECTED_INSTITUTION_NAME_KEY,
  MONEYKIT_OAUTH_TOKEN_KEY,
} from './moneykit-oauth-storage-keys';

export const CREATE_LINK_SESSION = graphql(/* GraphQL */ `
  mutation MoneykitCreateLinkSessionFromConnectButton($input: MoneykitCreateLinkSessionInput!) {
    moneykitCreateLinkSession(input: $input) {
      linkSessionToken
    }
  }
`);

export const EXCHANGE_LINK_TOKEN = graphql(/* GraphQL */ `
  mutation MoneykitExchangeLinkTokenFromConnectButton($input: MoneykitExchangeLinkTokenInput!) {
    moneykitExchangeLinkToken(input: $input) {
      providerAccount {
        id
        providerName
      }
      linkId
      institutionName
    }
  }
`);

export const REFRESH_LINK = graphql(/* GraphQL */ `
  mutation MoneykitRefreshLinkFromConnectButton($input: MoneykitRefreshLinkInput!) {
    moneykitRefreshLink(input: $input) {
      providerAccount {
        id
      }
    }
  }
`);

type MoneykitConnectButtonProps = {
  readonly title: string;
  readonly subtitle?: string;
  readonly onSuccess: Deprecated_HandleItemUpdate;
  readonly onError?: (error: LinkExitError) => void;
  readonly setIsLoading: (isLoading: boolean) => void;
  readonly buttonProps?: ButtonProps;
  readonly providerAccountId?: ID;
  readonly clientUserId?: ID;
  readonly requestHeaders?: Readonly<Record<string, string>>;
  // When set, the existing Plaid ProviderAccount with this ID will be deprecated
  // (not deleted) after the new MoneyKit account is created successfully.
  readonly replacingProviderAccountId?: ID;
};

function mutationContext(requestHeaders?: Readonly<Record<string, string>>) {
  if (requestHeaders === undefined || !hasMagicLinkAuthorizationHeader(requestHeaders)) {
    return undefined;
  }
  return { context: { headers: requestHeaders } };
}

/**
 * MoneyKit Connect launcher button. Mirrors the shape of PlaidLinkButton — same
 * onSuccess contract (Deprecated_HandleItemUpdate), same setIsLoading lifecycle —
 * so existing add-account flows can swap it in without rewriting consumer logic.
 *
 * Flow:
 *   1. Click → moneykitCreateLinkSession → returns link_session_token (JWT)
 *   2. Open @moneykit/connect SDK with that token
 *   3. SDK onLinkSuccess(exchangeableToken, institution, link_id) →
 *      moneykitExchangeLinkToken → ProviderAccount persisted, fan-out worker enqueued
 *   4. Notify parent via onSuccess so the existing post-success refresh path runs
 *
 * Re-link flow (when providerAccountId is set): same first three steps, but the
 * exchange path is moneykitRefreshLink instead. This mirrors PlaidLinkButton's
 * `updateExistingLinkedAccount`.
 */
export function MoneykitConnectButton({
  title,
  subtitle,
  onSuccess,
  onError,
  setIsLoading,
  buttonProps,
  providerAccountId,
  clientUserId,
  requestHeaders,
  replacingProviderAccountId,
}: MoneykitConnectButtonProps) {
  const [createLinkSession] = useMutationWithErrorLogging(CREATE_LINK_SESSION);
  const [exchangeLinkToken] = useMutationWithErrorLogging(EXCHANGE_LINK_TOKEN);
  const [refreshLink] = useMutationWithErrorLogging(REFRESH_LINK);
  const [isLoadingMoneykit, setIsLoadingMoneykit] = useState(false);
  const reportedFailureRef = useRef<MoneykitConnectFailure | null>(null);
  const keepAliveRef = useRef<(() => void) | null>(null);
  const trackInteraction = useTrackEvent('userInteraction');
  const connectParentName = providerAccountId !== undefined ? 'moneykit-relink' : 'moneykit-connect';
  // Mirror PlaidLinkButton: read-only household roles (beneficiary,
  // authorized viewer) must not be able to launch Connect — gate the CTA
  // disabled + tooltip rather than letting them fail at the mutation.
  const { canWrite, tooltip } = useHouseholdWriteAccess();
  const isMagicLinkRequest = requestHeaders !== undefined && hasMagicLinkAuthorizationHeader(requestHeaders);
  const canLaunchConnect = canWrite || isMagicLinkRequest;

  function releaseKeepAlive() {
    keepAliveRef.current?.();
    keepAliveRef.current = null;
  }

  useEffect(
    () => () => {
      releaseKeepAlive();
    },
    [],
  );

  function recordConnectFailure(failure: MoneykitConnectFailure) {
    if (reportedFailureRef.current !== null) return;
    reportedFailureRef.current = failure;
    reportMoneykitConnectFailure({ failure, flow: connectParentName });
    trackInteraction({
      elementName: 'moneykit-connect-failed',
      htmlElement: 'button',
      interactionType: 'connect_error',
      parentElementName: connectParentName,
    });
  }

  function handleConnectError(error: LinkExitError | null) {
    releaseKeepAlive();
    setIsLoading(false);
    setIsLoadingMoneykit(false);
    // Connect emits `error` before the user dismisses its failure screen, so by
    // exit time the richer payload is usually already recorded; only a failure
    // that skipped the event needs reporting here.
    const reported = reportedFailureRef.current;
    const failure = mergeMoneykitConnectFailure(reported, error);
    if (failure !== null) {
      if (reported === null) recordConnectFailure(failure);
      toast.error(moneykitConnectFailureMessage({ failure }));
    }
    if (error !== null && onError !== undefined) {
      onError(error);
    }
  }

  function handleLinkEvent(event: Record<string, unknown>) {
    const selectedName = readMoneykitConnectEventInstitutionName(event);
    if (selectedName !== null) {
      localStorage.setItem(MONEYKIT_OAUTH_SELECTED_INSTITUTION_NAME_KEY, selectedName);
    }
    const failure = readMoneykitConnectEventFailure(event);
    if (failure === null) return;
    recordConnectFailure(failure);
  }

  function handleConnectSuccess(exchangeableToken: string, institution: LinkedInstitution) {
    releaseKeepAlive();
    const selectedName = localStorage.getItem(MONEYKIT_OAUTH_SELECTED_INSTITUTION_NAME_KEY);
    const exchangeInput = buildMoneykitExchangeLinkVariables({
      exchangeToken: exchangeableToken,
      clientUserId: clientUserId?.toString(),
      replacingProviderAccountId: replacingProviderAccountId?.toString(),
      isMagicLinkRequest,
    });
    exchangeLinkToken({ variables: { input: exchangeInput }, ...mutationContext(requestHeaders) })
      .then((res) => {
        const exchange = res.data?.moneykitExchangeLinkToken;
        const id = exchange?.providerAccount.id;
        if (!id) {
          handleMutationError(
            new Error('missing provider account id'),
            "Couldn't connect your bank. Please try again.",
          );
          return;
        }
        trackInteraction({
          elementName: 'moneykit-connect-completed',
          htmlElement: 'button',
          interactionType: 'connect_success',
          parentElementName: connectParentName,
        });
        const namedProvider = readMoneykitInstitutionName({
          selectedName,
          linkedName: institution.name,
          institutionName: exchange.institutionName,
          providerName: exchange.providerAccount.providerName,
        });
        return onSuccess({
          type: UpdateType.CREATE_PROVIDER_ACCOUNT,
          id,
          ...(namedProvider !== null ? { providerName: namedProvider } : {}),
        });
      })
      .catch((e: unknown) => {
        console.error(e);
        handleMutationError(e, "Couldn't connect your bank. Please try again.");
      })
      .finally(() => {
        localStorage.removeItem(MONEYKIT_OAUTH_SELECTED_INSTITUTION_NAME_KEY);
        setIsLoading(false);
        setIsLoadingMoneykit(false);
      });
  }

  // RelinkSuccessCallback signature: (institution, link_id) — no
  // exchangeable_token because relink refreshes credentials on an existing
  // link in place. Tell Rails to refresh data via moneykitRefreshLink.
  function handleRelinkSuccess(_institution: LinkedInstitution, _linkId: string) {
    releaseKeepAlive();
    if (!providerAccountId) {
      setIsLoading(false);
      setIsLoadingMoneykit(false);
      return;
    }
    refreshLink({
      variables: {
        input: {
          providerAccountId: providerAccountId.toString(),
        },
      },
      ...mutationContext(requestHeaders),
    })
      .then((res) => {
        const id = res.data?.moneykitRefreshLink?.providerAccount.id;
        if (!id) {
          toast.error("Couldn't refresh your bank connection. Please try again.");
          return;
        }
        trackInteraction({
          elementName: 'moneykit-connect-completed',
          htmlElement: 'button',
          interactionType: 'connect_success',
          parentElementName: connectParentName,
        });
        return onSuccess({ type: UpdateType.UPDATE_PROVIDER_ACCOUNT, id });
      })
      .catch((e: unknown) => {
        console.error(e);
        handleMutationError(e, "Couldn't refresh your bank connection. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMoneykit(false);
      });
  }

  function onClick() {
    if (isLoadingMoneykit) {
      return;
    }
    setIsLoadingMoneykit(true);
    setIsLoading(true);

    trackInteraction({
      elementName: 'moneykit-connect-started',
      htmlElement: 'button',
      interactionType: 'button_click',
      parentElementName: connectParentName,
    });

    const input: VariablesOf<typeof CREATE_LINK_SESSION>['input'] = {
      // OAuth banks redirect the browser back here, so it must match
      // whatever origin the web app is currently served on (3000, 3002,
      // preview Vercel URL, etc.). The Rails default redirect URI is a
      // last-resort fallback only.
      redirectUri: `${window.location.origin}/moneykit-redirect`,
    };
    if (clientUserId && !isMagicLinkRequest) {
      input.userId = clientUserId.toString();
    }
    if (providerAccountId) {
      // Pass the existing link ID so MoneyKit creates a reauth session
      // with existing_link_id, allowing Connect to re-authorise the same
      // connection rather than creating a new one.
      input.providerAccountId = providerAccountId.toString();
    }
    createLinkSession({ variables: { input }, ...mutationContext(requestHeaders) })
      .then((res) => {
        const token = res.data?.moneykitCreateLinkSession?.linkSessionToken;
        if (!token) {
          setIsLoadingMoneykit(false);
          setIsLoading(false);
          return;
        }
        // Persist context so /moneykit-redirect can resume the SDK after the
        // bank's OAuth redirect lands the browser back here.
        localStorage.setItem(MONEYKIT_OAUTH_TOKEN_KEY, token);
        localStorage.setItem(MONEYKIT_OAUTH_RETURN_PAGE_KEY, window.location.href);
        localStorage.removeItem(MONEYKIT_OAUTH_SELECTED_INSTITUTION_NAME_KEY);
        if (clientUserId && !isMagicLinkRequest) {
          localStorage.setItem(MONEYKIT_OAUTH_CLIENT_USER_ID_KEY, clientUserId.toString());
        } else {
          localStorage.removeItem(MONEYKIT_OAUTH_CLIENT_USER_ID_KEY);
        }
        if (providerAccountId) {
          localStorage.setItem(MONEYKIT_OAUTH_PROVIDER_ACCOUNT_ID_KEY, providerAccountId.toString());
        } else {
          localStorage.removeItem(MONEYKIT_OAUTH_PROVIDER_ACCOUNT_ID_KEY);
        }
        if (replacingProviderAccountId && !isMagicLinkRequest) {
          localStorage.setItem(MONEYKIT_OAUTH_REPLACING_PROVIDER_ACCOUNT_ID_KEY, replacingProviderAccountId.toString());
        } else {
          localStorage.removeItem(MONEYKIT_OAUTH_REPLACING_PROVIDER_ACCOUNT_ID_KEY);
        }
        try {
          reportedFailureRef.current = null;
          const moneykit = new MoneyKit();
          if (providerAccountId) {
            moneykit.relink(token, handleRelinkSuccess, handleConnectError, handleLinkEvent);
          } else {
            moneykit.link(token, handleConnectSuccess, handleConnectError, handleLinkEvent);
          }
          fitMoneykitConnectFrameToViewport();
          keepAliveRef.current = startMoneykitConnectKeepAlive();
        } catch (e: unknown) {
          console.error(e);
          releaseKeepAlive();
          setIsLoadingMoneykit(false);
          setIsLoading(false);
        }
      })
      .catch((e: unknown) => {
        console.error(e);
        handleMutationError(e, "Couldn't start bank connection. Please try again.");
        setIsLoadingMoneykit(false);
        setIsLoading(false);
      });
  }

  function renderButton() {
    if (buttonProps) {
      return (
        <TrackedButton
          elementName="moneykit-connect-button"
          {...buttonProps}
          onClick={onClick}
          isLoading={isLoadingMoneykit}
          disabled={buttonProps.disabled || !canLaunchConnect}
        >
          {title}
        </TrackedButton>
      );
    }
    return (
      <MoneykitConnectDefaultButton
        onClick={onClick}
        loading={isLoadingMoneykit}
        icon={<MoneykitGlyph color={primaryColors.contrastText} />}
        title={title}
        subtitle={subtitle}
        disabled={!canLaunchConnect}
      />
    );
  }

  function renderWithTooltip() {
    if (canLaunchConnect || tooltip === null) {
      return renderButton();
    }
    return (
      <Tooltip>
        <TooltipTrigger render={<span data-testid="moneykit-connect-button-gated" />}>{renderButton()}</TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return renderWithTooltip();
}

// Inline glyph so PR 2d doesn't depend on a new icon asset. Cutover may swap
// for a polished MoneyKit logo if/when the brand wants one.
function MoneykitGlyph({ color }: { readonly color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="3" width="12" height="10" rx="2" stroke={color} strokeWidth="1.5" />
      <path d="M2 7H14" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}
